import React from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';

import Badge from '@decisiv/ui-components/lib/components/Badge';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3 } from '@decisiv/ui-components/lib/components/Typography';
import ExclamationTriangle from '@decisiv/iconix/lib/components/ExclamationTriangle';

import ROUTE_NAMES from 'setup/Routing/routeNames';

import Card from 'components/DashboardCard';

import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';

import Loading from 'components/Loading';
import Divider from 'components/Divider';

import isNull from 'lodash/isNull';
import useAlertsConfiguration from 'hooks/useAlertsConfiguration';
import { Grid } from './styles';

import LinkOrLabel from '../LinkOrLabel';
import SeverityColumn from './SeverityColumn';

const dashboardFaultsBySeverityQuery = loader(
  './dashboardFaultsBySeverity.graphql',
);

const Paper = styled(Flex).attrs({ padding: 2 })`
  width: 100%;
  box-shadow: 0 1px 4px rgba(37, 38, 46, 0.4);
  border-radius: 2px;
`;

const getCountText = (loading, total) => {
  /* istanbul ignore next -- @preserve */
  if (loading) return '';

  return isNull(total) ? /* istanbul ignore next -- @preserve */ '―' : total;
};

const getBadgeColor = (total) => {
  return isNull(total)
    ? /* istanbul ignore next -- @preserve */ 'licoriceMousse'
    : 'information';
};

const infoText = t`View the total number of faults organized by fault severity.`;

const SEVERITY_CONFIG = [
  {
    id: 'SEVERE',
    key: 'severe',
    text: t`Severe`,
    to: `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=SEVERE`,
  },
  {
    id: 'ATTENTION',
    key: 'attention',
    text: t`Attention`,
    to: `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=ATTENTION`,
  },
  {
    id: 'MODERATE',
    key: 'moderate',
    text: t`Moderate`,
    to: `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=MODERATE`,
  },
  {
    id: 'UNKNOWN',
    key: 'unknown',
    text: t`Unknown`,
    to: `${ROUTE_NAMES.alerts}?alertTypes=faults&faultStatus=UNKNOWN`,
  },
];

const getEnabledTotal = (severities) => {
  if (!severities.length) return 0;
  if (severities.every(({ count }) => isNull(count))) return null;

  return severities.reduce((sum, { count }) => sum + (count || 0), 0);
};

export default function FaultsBySeverityCard() {
  const { faultsEnabled, faultSeveritiesEnabled } = useAlertsConfiguration();

  const enabledFaultSeverities = (faultSeveritiesEnabled || []).filter(
    (severity) => SEVERITY_CONFIG.some(({ id }) => id === severity),
  );

  const {
    loading,
    data: {
      dashboardFaultsBySeverity: {
        total,
        severe,
        moderate,
        attention,
        unknown,
      } = {},
    } = {},
  } = useQuery(dashboardFaultsBySeverityQuery, {
    skip: !faultsEnabled,
    variables: SEVERITY_CONFIG.reduce(
      (vars, { id, key }) => ({
        ...vars,
        [key]: enabledFaultSeverities.includes(id),
      }),
      {},
    ),
  });

  if (!faultsEnabled) return null;

  const totalFaultsLink =
    enabledFaultSeverities.length > 0
      ? `${
          ROUTE_NAMES.alerts
        }?alertTypes=faults&faultStatus=${enabledFaultSeverities.join(',')}`
      : `${ROUTE_NAMES.alerts}?alertTypes=faults`;

  const severities = SEVERITY_CONFIG.filter(({ id }) =>
    enabledFaultSeverities.includes(id),
  ).map(({ key, text, to }) => ({
    severity: key,
    count: {
      severe,
      moderate,
      attention,
      unknown,
    }[key],
    text,
    to,
  }));

  const totalEnabledFaults = getEnabledTotal(severities);

  const totalFaultsLabel = t`Fault Alerts`;

  return (
    <Card text={t`Faults by Severity`} infoText={infoText} marginTop={0}>
      {loading && <Loading />}
      {!loading && (
        <Flex flexDirection="row">
          <Paper marginY={1}>
            <Grid>
              <Flex alignItems="top">
                <Badge
                  variant="square"
                  icon={ExclamationTriangle}
                  color={getBadgeColor(total)}
                />
              </Flex>
              <Flex flexDirection="column" marginLeft={1}>
                <H3 weight="regular">
                  {getCountText(loading, totalEnabledFaults ?? total)}
                </H3>
                <LinkOrLabel
                  count={totalEnabledFaults ?? total}
                  to={totalFaultsLink}
                  text={totalFaultsLabel}
                />
              </Flex>
              <Divider
                css={`
                  height: auto;
                `}
                marginX={1}
              />
              <SeverityColumn
                severities={severities.slice(0, 2)}
                loading={loading}
                marginBottom={2}
              />
              <SeverityColumn
                severities={severities.slice(2)}
                loading={loading}
              />
            </Grid>
          </Paper>
        </Flex>
      )}
    </Card>
  );
}
